import {
  COLOR_CONFLICT_FLAG,
  G_ATTRIBUTES,
  G_LICENSE_USE_APPROVAL_DISAPPROVAL,
  LOCAL_STORAGE,
  VERIFICATION_TYPE,
} from "@constants/index";
import { rolePriority } from "@constants/project";
import axios from "axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { getLocalStorage } from "./local-storage";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { extractErrorCode } from "./error";
import i18n from "i18next";
import { buildSbomDownloadPayload, getSbomExportResponseType, resolveSbomExportLanguage } from "@constants/sbom";

// Notice Status Constants
export const NOTICE_STATUS = {
  ON_WRITE: "ON_WRITE",
  ON_REVIEW: "ON_REVIEW",
  DONE: "DONE",
  REQUEST: "REQUEST",
  UNNECESSARY: "UNNECESSARY",
};

dayjs.extend(duration);

export const isIterableArray = (array) => Array.isArray(array) && !!array.length;

/** verification_report API: map selected list row + optional page context (e.g. CLARITYAIR) */
export function resolveVerificationToolCdForReport(selectedFile, contextVerificationToolCd) {
  if (!selectedFile) {
    return contextVerificationToolCd || VERIFICATION_TYPE.FOSSID;
  }
  const vt = selectedFile.verificationType;
  if (vt === "clarity") return VERIFICATION_TYPE.CLARITY;
  if (vt === "fossid") return VERIFICATION_TYPE.FOSSID;
  if (vt === "clarity_air" || vt === "clarityair") return VERIFICATION_TYPE.CLARITYAIR;
  if (selectedFile.clarityFlag === "Y") return VERIFICATION_TYPE.CLARITY;
  if (contextVerificationToolCd === VERIFICATION_TYPE.CLARITYAIR) return VERIFICATION_TYPE.CLARITYAIR;
  if (contextVerificationToolCd === VERIFICATION_TYPE.SBOM) return VERIFICATION_TYPE.SBOM;
  return VERIFICATION_TYPE.FOSSID;
}

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540,
};

export const buildRequestBody = (searchFilters, itemsPerPage, currentPage) => {
  const bodyRequest = {};
  Object.keys(searchFilters).forEach((key) => {
    if (searchFilters[key]) bodyRequest[key] = searchFilters[key];
  });
  bodyRequest.pageSize = itemsPerPage;
  bodyRequest.startRow = itemsPerPage * (currentPage - 1);
  return bodyRequest;
};

export const getItemFromStore = (key, defaultValue, store = localStorage) => {
  try {
    return store.getItem(key) === null ? defaultValue : JSON.parse(store.getItem(key));
  } catch {
    return store.getItem(key) || defaultValue;
  }
};

export const setItemToStore = (key, payload, store = localStorage) => store.setItem(key, payload);

export const getStoreSpace = (store = localStorage) =>
  parseFloat((escape(encodeURIComponent(JSON.stringify(store))).length / (1024 * 1024)).toFixed(2));

//===============================
// Cookie
//===============================
export const getCookieValue = (name) => {
  const value = document.cookie.match("(^|[^;]+)\\s*" + name + "\\s*=\\s*([^;]+)");
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/";
};

// Session cookie (expires when browser closes)
export const createSessionCookie = (name, value) => {
  document.cookie = name + "=" + value + "; path=/";
};

export const removeCookie = (name) => {
  document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
};

export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + "B"
    : // Six Zeroes for Millions
      Math.abs(Number(number)) >= 1.0e6
      ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + "M"
      : // Three Zeroes for Thousands
        Math.abs(Number(number)) >= 1.0e3
        ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + "K"
        : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// Colors
//===============================
export const hexToRgb = (hexValue) => {
  let hex;
  hexValue.indexOf("#") === 0 ? (hex = hexValue.substring(1)) : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b),
  );
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) => `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
  "#2c7be5",
  "#00d97e",
  "#e63757",
  "#39afd1",
  "#fd7e14",
  "#02a8b5",
  "#727cf5",
  "#6b5eae",
  "#ff679b",
  "#f6c343",
];

export const themeColors = {
  primary: "#2c7be5",
  secondary: "#748194",
  success: "#00d27a",
  info: "#27bcfd",
  warning: "#f5803e",
  danger: "#e63757",
  light: "#f9fafd",
  dark: "#0b1727",
};

export const grays = {
  white: "#fff",
  100: "#f9fafd",
  200: "#edf2f9",
  300: "#d8e2ef",
  400: "#b6c1d2",
  500: "#9da9bb",
  600: "#748194",
  700: "#5e6e82",
  800: "#4d5969",
  900: "#344050",
  1000: "#232e3c",
  1100: "#0b1727",
  black: "#000",
};

export const darkGrays = {
  white: "#fff",
  1100: "#f9fafd",
  1000: "#edf2f9",
  900: "#d8e2ef",
  800: "#b6c1d2",
  700: "#9da9bb",
  600: "#748194",
  500: "#5e6e82",
  400: "#4d5969",
  300: "#344050",
  200: "#232e3c",
  100: "#0b1727",
  black: "#000",
};

export const getGrays = (isDark) => (isDark ? darkGrays : grays);

export const rgbColors = colors.map((color) => rgbColor(color));
export const rgbaColors = colors.map((color) => rgbaColor(color));

export const getColor = (name) => {
  const dom = document.documentElement;
  return getComputedStyle(dom).getPropertyValue(`--falcon-${name}`).trim();
};

//===============================
// Echarts
//===============================
export const getPosition = (pos, params, dom, rect, size) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2,
});
//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) => (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find((item) => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);
export const getSubtotal = (items) => items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);
export const getDiscountPrice = (total, discount) => total - total * (discount / 100);

export const getProductsQuantity = (products) => products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str) => (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, " ");

export const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const uppercase = (str) => {
  if (typeof str !== "string") return str;
  return str.toUpperCase();
};

export const camelize = (str) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const dashed = (str) => {
  return str.toLowerCase().replaceAll(" ", "-");
};

//routes helper

export const flatRoutes = (childrens) => {
  const allChilds = [];

  const flatChild = (childrens) => {
    childrens.forEach((child) => {
      if (child.children) {
        flatChild(child.children);
      } else {
        allChilds.push(child);
      }
    });
  };
  flatChild(childrens);

  return allChilds;
};

export const getFlatRoutes = (children) =>
  children.reduce(
    (acc, val) => {
      if (val.children) {
        return {
          ...acc,
          [camelize(val.name)]: flatRoutes(val.children),
        };
      } else {
        return {
          ...acc,
          unTitled: [...acc.unTitled, val],
        };
      }
    },
    { unTitled: [] },
  );

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map((route) => {
    if (route.children) {
      return route.children.map((item) => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName) => {
  return window.location.pathname.split("/").slice(-1)[0] === pageName;
};

export const copyToClipBoard = (textFieldRef) => {
  const textField = textFieldRef.current;
  textField.focus();
  textField.select();
  document.execCommand("copy");
};

export const reactBootstrapDocsUrl = "https://react-bootstrap.github.io";

export const pagination = (currentPage, size) => {
  const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  let prev = currentPage - 1 - Math.floor(size / 2);

  if (currentPage - 1 - Math.floor(size / 2) < 0) {
    prev = 0;
  }
  if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
    prev = pages.length - size;
  }
  const next = prev + size;

  return pages.slice(prev, next);
};

export const tooltipFormatter = (params) => {
  let tooltipItem = "";
  params.forEach((el) => {
    tooltipItem =
      tooltipItem +
      `<div class='ms-1'> 
        <h6 class="text-700"><span class="fas fa-circle me-1 fs-11" style="color:${
          el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${typeof el.value === "object" ? el.value[1] : el.value}
        </h6>
      </div>`;
  });
  return `<div>
            <p class='mb-2 text-600'>
              ${
                dayjs(params[0].axisValue).isValid()
                  ? dayjs(params[0].axisValue).format("MMMM DD")
                  : params[0].axisValue
              }
            </p>
            ${tooltipItem}
          </div>`;
};

export const addIdField = (items) => {
  return items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));
};

// get file size

export const getSize = (size) => {
  if (size < 1024) {
    return `${size} Byte`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/* Get A Random Number */
export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min) + min);
};

/* get Dates between */

export const getDates = (startDate, endDate, interval = 1000 * 60 * 60 * 24) => {
  const duration = endDate - startDate;
  const steps = duration / interval;
  return Array.from({ length: steps + 1 }, (v, i) => new Date(startDate.valueOf() + interval * i));
};

/* Get Past Dates */
export const getPastDates = (duration) => {
  let days;

  switch (duration) {
    case "week":
      days = 7;
      break;
    case "month":
      days = 30;
      break;
    case "year":
      days = 365;
      break;

    default:
      days = duration;
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

// Add id to items in array
export const addId = (items) =>
  items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));

//
export const getTimeDuration = (startDate, endDate, format = "") => {
  return dayjs.duration(endDate.diff(startDate)).format(format);
};

// Get Percentage
export const getPercentage = (number, percent) => {
  return (Number(number) / 100) * Number(percent);
};

//get chunk from array
export const chunk = (arr, chunkSize = 1, cache = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};

// Slugify text
export const slugifyText = (str) =>
  str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

// Convert timestamp to readable date format
export const convertTimeStamp = (timeSt) => {
  return dayjs(timeSt).format("YYYY-MM-DD HH:mm:ss");
};

// Convert timestamp to readable date format
export const setBackgroundScanType = (scanType) => {
  switch (scanType) {
    case "S":
      return "#cd5c5c";
    case "B":
      return "#4682B4";
    default:
      return "#ffffff";
  }
};

export function getLicensePolicyResult(licensePolicy, licenseAttribute) {
  // No license attribute → white (undefined license); conflict column stays gray
  const result = {
    policyStep: 0,
    policyStepColor: COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION,
    policyConflictColor: COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT,
    conflictDetails: [],
  };

  if (!licenseAttribute) {
    return result;
  }

  // Attribute exists but no policy steps to evaluate → gray (undefined obligation type)
  if (!licensePolicy?.licensePolicySteps?.length) {
    result.policyStepColor = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
    result.policyConflictColor = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
    return result;
  }

  // Map attribute to match conditions for faster lookup
  const attributeMap = {
    ATTR_01: licenseAttribute.attr1 === "O",
    ATTR_02_ALL: licenseAttribute.attr2 === "O" && licenseAttribute.attr13 === "ALL",
    ATTR_02_FILE: licenseAttribute.attr2 === "O" && licenseAttribute.attr13 === "FILE",
    ATTR_02_LIBRARY: licenseAttribute.attr2 === "O" && licenseAttribute.attr13 === "LIBRARY",
    ATTR_02_MODULE: licenseAttribute.attr2 === "O" && licenseAttribute.attr13 === "MODULE",
    ATTR_03: licenseAttribute.attr3 === "O",
    ATTR_04: licenseAttribute.attr4 === "O",
    ATTR_05: licenseAttribute.attr5 === "O",
    ATTR_06: licenseAttribute.attr6 === "O",
    ATTR_07: licenseAttribute.attr7 === "O",
    ATTR_08: licenseAttribute.attr8 === "O",
    ATTR_09: licenseAttribute.attr9 === "O",
    ATTR_10: licenseAttribute.attr10 === "O",
    ATTR_11: licenseAttribute.attr11 === "O",
    ATTR_12: licenseAttribute.attr12 === "O",
    ATTR_13: ["ALL", "FILE", "LIBRARY", "MODULE", "MPL"].includes(licenseAttribute.attr13),
  };

  let policyStepTop = 9;
  let policyStepColorTop = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;

  // Process each policy step
  for (const licensePolicyStep of licensePolicy.licensePolicySteps) {
    const conflictDetail = {
      policyStep: licensePolicyStep.policyStep,
      licenseUseApprovalCd: licensePolicyStep.licenseUseApprovalCd,
      remark: licensePolicyStep.remark,
      attributes: [],
    };

    let foundMatch = false;

    // Check each policy step item against our attribute map
    for (const item of licensePolicyStep.licensePolicyStepItems || []) {
      if (attributeMap[item.licensePolicyCd]) {
        foundMatch = true;
        conflictDetail.attributes.push(item.licensePolicyCd);
      }
    }

    if (foundMatch) {
      // Keep track of the highest priority (lowest number) policy step
      if (licensePolicyStep.policyStep < policyStepTop) {
        policyStepTop = licensePolicyStep.policyStep;
        policyStepColorTop = licensePolicyStep.policyStepColor;
      }

      result.conflictDetails.push(conflictDetail);
    }
  }

  if (result.conflictDetails.length > 0) {
    result.policyStep = policyStepTop;
    result.policyStepColor = policyStepColorTop;
  } else {
    // Attribute exists but no policy step matched → undefined obligation type (gray)
    result.policyStep = 0;
    result.policyStepColor = COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;
  }

  // Check for disapproval conflicts
  result.policyConflictColor = result.conflictDetails.some((detail) => detail.licenseUseApprovalCd === "DISAPPROVAL")
    ? "darkred"
    : COLOR_CONFLICT_FLAG.UNDEFINED_CONFLICT;

  return result;
}

export function getLicensePolicyRule(licensePolicyRules, scanComponent) {
  if (licensePolicyRules) {
    for (let i = 0; i < licensePolicyRules.length; i++) {
      let lpr = licensePolicyRules[i];

      if (
        (scanComponent.componentLicense == lpr.licenseIdentifier ||
          scanComponent.licenseName == lpr.licenseIdentifier) &&
        scanComponent.matchTypeCd == lpr.matchTypeCd &&
        scanComponent.modificationFlag == lpr.modificationFlag &&
        scanComponent.patentFlag == lpr.patentFlag
      ) {
        return lpr;
      }
    }
  }

  /*
	return {
		policyStep : 0,                //정책 단계
		policyStepColor : "white",     //정책 단계 색상
		licenseUseApprovalCd : "Y"     //라이선스 사용승인 코드
	};
	*/

  return null;
}

/**
 * Checks for license conflicts between a component and a list of other components.
 * @param {Array} scanComponents - Array of components to check against
 * @param {Object} scanComponent - The component to check for conflicts
 * @returns {Object} Result object with conflict status and details
 */
export function getComponentLicenseConflict(scanComponents, scanComponent) {
  // Extract component and license names with fallbacks
  const componentLicense = scanComponent.componentLicense || scanComponent.licenseName || "";
  const component = scanComponent.component || scanComponent.componentName || "";

  // Initialize the result object
  const result = {
    conflict: false,
    component: {
      component,
      componentLicense,
    },
    conflictComponents: [],
  };

  // Early return if there's no license attribute or component license
  if (!scanComponent.licenseAttribute || !componentLicense) {
    return result;
  }

  // Create attribute array for the current component for easier comparison
  const c1 = scanComponent;
  const componentLicense1 = c1.componentLicense || c1.licenseName || "";
  const ca1 = [
    "",
    c1.licenseAttribute.attr1,
    c1.licenseAttribute.attr2,
    c1.licenseAttribute.attr3,
    c1.licenseAttribute.attr4,
    c1.licenseAttribute.attr5,
    c1.licenseAttribute.attr6,
    c1.licenseAttribute.attr7,
    c1.licenseAttribute.attr8,
    c1.licenseAttribute.attr9,
    c1.licenseAttribute.attr10,
    c1.licenseAttribute.attr11,
    c1.licenseAttribute.attr12,
    c1.licenseAttribute.attr13,
  ];

  // Compare with each component in the list
  for (const c2 of scanComponents) {
    const componentLicense2 = c2.componentLicense || c2.licenseName || "";
    const component2 = c2.component || c2.componentName || "";

    // Skip if the component has no license attribute or license name
    if (!c2.licenseAttribute || !componentLicense2) {
      continue;
    }

    // Skip comparing with the same license
    if (componentLicense1 === componentLicense2) {
      continue;
    }

    // Create attribute array for the comparison component
    const ca2 = [
      "",
      c2.licenseAttribute.attr1,
      c2.licenseAttribute.attr2,
      c2.licenseAttribute.attr3,
      c2.licenseAttribute.attr4,
      c2.licenseAttribute.attr5,
      c2.licenseAttribute.attr6,
      c2.licenseAttribute.attr7,
      c2.licenseAttribute.attr8,
      c2.licenseAttribute.attr9,
      c2.licenseAttribute.attr10,
      c2.licenseAttribute.attr11,
      c2.licenseAttribute.attr12,
      c2.licenseAttribute.attr13,
    ];

    let conflict = false;

    // Check attribute 1 compatibility
    if (ca1[1] !== ca2[1] && ca1[1] !== "X" && ca2[1] !== "X") {
      conflict = true;
    }

    // Check attributes 2-5 compatibility
    for (let x = 2; x <= 5; x++) {
      if (ca1[x] !== ca2[x] && ca1[x] !== "M" && ca2[x] !== "M") {
        conflict = true;
      }
    }

    // Check attributes 6-12 compatibility
    for (let x = 6; x <= 12; x++) {
      if (ca1[x] !== ca2[x] && ca1[x] !== "X" && ca2[x] !== "X") {
        conflict = true;
      }
    }

    // Check special case for attribute 12 and 13
    if (ca1[12] === "O" && ca1[13] !== "X" && ca2[12] === "O" && ca2[13] !== "X") {
      if (ca1[13] !== ca2[13]) {
        conflict = true;
      }
    }

    // Check special case for attribute 2 and 13
    if (ca1[2] === "O" && ca1[13] !== "X" && ca2[2] === "O" && ca2[13] !== "X") {
      if (ca1[13] !== ca2[13]) {
        conflict = true;
      }
    }

    // If conflict found, add to result
    if (conflict) {
      result.conflict = true;
      result.conflictComponents.push({
        component: component2,
        componentLicense: componentLicense2,
      });
    }
  }

  return result;
}

/**
 * Checks if a component has conflicts and sets conflictFlag to "Y" if conflicts exist
 * @param {Array} scanComponents - Array of all components to check against
 * @param {Object} scanComponent - The component to check for conflicts
 * @param {Object} licensePolicyData - License policy data for checking policy conflicts
 * @returns {Object} The component with updated conflictFlag
 */
export function updateComponentConflictFlag(scanComponents, scanComponent, licensePolicyData) {
  // Create a copy of the component to avoid mutating the original
  const updatedComponent = { ...scanComponent };

  // Check for license policy conflicts
  let hasPolicyConflict = false;
  if (licensePolicyData && scanComponent.licenseAttribute) {
    const licensePolicyResult = getLicensePolicyResult(licensePolicyData, scanComponent.licenseAttribute);

    // Check if there's a specific rule that applies
    const licensePolicyRule = getLicensePolicyRule(licensePolicyData?.licensePolicyRules, scanComponent);
    if (licensePolicyRule != null) {
      if (licensePolicyRule.licenseUseApprovalCd === "DISAPPROVAL") {
        hasPolicyConflict = true;
      }
    } else {
      // Check for disapproval conflicts in policy result
      hasPolicyConflict = licensePolicyResult.conflictDetails.some(
        (detail) => detail.licenseUseApprovalCd === "DISAPPROVAL",
      );
    }
  }

  // Check for component license conflicts
  const componentConflictResult = getComponentLicenseConflict(scanComponents, scanComponent);
  const hasComponentConflict = componentConflictResult.conflict;


  // Set conflictFlag to "Y" if there are any conflicts
  if (hasPolicyConflict) {
    updatedComponent.conflictFlag = "Y";
    updatedComponent.projectLicenseConflictFlag = "Y";
  } else if (hasComponentConflict) {
    updatedComponent.conflictFlag = "Y";
    updatedComponent.componentLicenseConflictFlag = "Y";
  } else {
    updatedComponent.conflictFlag = "N";
    updatedComponent.projectLicenseConflictFlag = "N";
    updatedComponent.componentLicenseConflictFlag = "N";
  }
  return updatedComponent;
}

/**
 * Processes an array of components and updates their conflictFlag based on conflicts
 * @param {Array} scanComponents - Array of components to process
 * @param {Object} licensePolicyData - License policy data for checking policy conflicts
 * @returns {Array} Array of components with updated conflictFlag
 */
export function processComponentConflictFlags(scanComponents, licensePolicyData) {
  if (!Array.isArray(scanComponents)) {
    return scanComponents;
  }

  return scanComponents.map((component) => updateComponentConflictFlag(scanComponents, component, licensePolicyData));
}

export const attributeMap = new Map(G_ATTRIBUTES.map((item) => [item.code, item]));

export function getNameByAttributeCode(codeValue) {
  const attribute = attributeMap.get(codeValue);
  return attribute ? attribute.name : "N/A";
}

export function getDescriptionByAttributeCode(codeValue) {
  const attribute = attributeMap.get(codeValue);
  return attribute ? attribute.remark : "N/A";
}
export const getPaginatedData = (items, page, itemsPerPage) => {
  const totalItems = items.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = items.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  return { currentData, totalItems, totalPages };
};

// Add a function to initialize data with default values for fields that are null
export const initializeComponentData = (data) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => {
    return {
      ...item,
      modificationFlag: item.modificationFlag || "N",
      sourceOpenFlag: item.sourceOpenFlag || "N",
      noticeFlag: item.noticeFlag || "N",
      patentFlag: item.patentFlag || "N",
      matchTypeCd: item.matchTypeCd || "",
    };
  });
};

export const getConstantLabel = (code, category) => {
  if (!code) return "";
  return category.find((type) => type.value.toUpperCase() === code.toUpperCase())?.label || code;
};

export const formatDate = (input) => {
  if (input) {
    const date = new Date(input);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } else {
    return "-";
  }
};

export const formatDateWithTime = (input) => {
  if (input) {
    const date = new Date(input);

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${month} ${day}, ${year} - ${hours}:${minutes}`;
  } else {
    return "-";
  }
};

export const formatDateTime = (input) => {
  if (input) {
    const date = new Date(input);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } else {
    return "-";
  }
};

export const formatFullDateTime = (input) => {
  if (input) {
    const date = new Date(input);
    const currentLang = i18n.language;
    const locale = currentLang || "en";

    const weekday = date.toLocaleDateString(locale, { weekday: "long" });
    const dateStr = date
    .toLocaleString("en-US", {
      year: "numeric", // Year (e.g., 2025)
      month: "2-digit", // Month (e.g., 10)
      day: "2-digit", // Day (e.g., 06)
      weekday: "long", // Weekday (e.g., Monday)
      hour: "2-digit", // Hour (24-hour format)
      minute: "2-digit",  // Minute
      second: "2-digit", // Second
      hour12: false, // Use 24-hour format
    })
    .replace(/(\d+)\/(\d+)\/(\d+)/, "$3-$1-$2")
    
    return `${weekday}, ${dateStr.split(", ").slice(1).join(", ")}`;
  } else {
    return "-";
  }
};

// Helper function to convert local date to UTC
export const convertLocalDateStringToUTC = (dateString) => {
  const localDate = new Date(dateString);
  return new Date(Date.UTC(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth(),
    localDate.getUTCDate(),
    localDate.getUTCHours(),
    localDate.getUTCMinutes(),
    localDate.getUTCSeconds()
  ));
};

export const getAppLanguage = () => getLocalStorage(LOCAL_STORAGE.LANGUAGE) || "en";

export const downloadFile = async (filename, download_url) => {
  try {
    const token = getLocalStorage(LOCAL_STORAGE.TOKEN);

    const response = await axios(download_url, {
      responseType: "blob",
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const blob = await response.data;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/** Parse download filename from Content-Disposition (RFC 5987 filename*, else quoted filename=). */
export const parseDownloadFilename = (contentDisposition) => {
  if (!contentDisposition) {
    return null;
  }
  const starred = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;\s]+)/i);
  if (starred?.[1]) {
    try {
      return decodeURIComponent(starred[1]);
    } catch {
      return starred[1];
    }
  }
  const quoted = contentDisposition.match(/(?:^|;)\s*filename\s*=\s*"([^"]+)"/i);
  return quoted?.[1] ?? null;
};

export const downloadBySource = async (filename, download_url, source, seq, repositoryImportFlag = "N") => {
  try {
    const token = getLocalStorage(LOCAL_STORAGE.TOKEN);

    const response = await axios(download_url, {
      responseType: "blob",
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        source,
        seq,
      },
    });
    const blob = await response.data;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Caller already has realFileName from API; do not re-parse Content-Disposition.
    const finalFilename = repositoryImportFlag === "Y"
      ? (filename.toLowerCase().endsWith(".zip") ? filename : filename + ".zip")
      : filename;
    a.download = finalFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const downloadFileDynamic = async (filename, download_url, params = {}) => {
  try {
    const token = getLocalStorage(LOCAL_STORAGE.TOKEN);
    const format = params.format || "JSON";
    const response = await axios.get(download_url, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        ...params,
        language: resolveSbomExportLanguage(format, params.language ?? getAppLanguage()),
      },
      responseType: getSbomExportResponseType(format),
    });

    if (response.status === 200) {
      const { blob } = buildSbomDownloadPayload(response.data, format);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
    const errorCodeKey = extractErrorCode(error, "errorcode.00001");
    const errorMessage = errorCodeKey ? i18n.t(errorCodeKey) : (error.response?.data?.message || error.response?.data?.additionalMessage || i18n.t("toast.export.error.message"));
    showErrorToast(i18n.t("toast.export.error.title"), errorMessage);
  }
};

export const normalizeResponseData = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) =>
      Object.keys(item).reduce((acc, key) => {
        acc[key] = item[key] ?? "";
        return acc;
      }, {}),
    );
  } else if (typeof data === "object" && data !== null) {
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = data[key] ?? "";
      return acc;
    }, {});
  }
  return data;
};

export const formatFileSize = (bytes, decimalPoint = 2) => {
  if (bytes === 0) return "0 Bytes";

  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${(bytes / Math.pow(1024, i)).toFixed(decimalPoint)} ${units[i]}`;
};

export const getHighestRole = (userRoles) => {
  if (!userRoles || userRoles.length === 0) {
    return null;
  }

  let highestRole = null;
  let highestPriority = 0;

  for (const role of userRoles) {
    if (rolePriority[role] > highestPriority) {
      highestPriority = rolePriority[role];
      highestRole = role;
    }
  }

  return highestRole;
};

export const renderLicensePolicyConflict = (currentData, licensePolicyData) => {
  const licensePolicyResults = [];
  if (currentData && licensePolicyData) {
    for (let item of currentData) {
      const licensePolicyResult = getLicensePolicyResult(licensePolicyData, item.licenseAttribute);
      let { policyConflictColor, policyStepColor, policyStep, conflictDetails } = licensePolicyResult;
      const licensePolicyRule = getLicensePolicyRule(licensePolicyData?.licensePolicyRules, item);
      if (licensePolicyRule != null) {
        policyConflictColor = "white";

        policyStepColor = licensePolicyRule.policyStepColor;
        policyStep = licensePolicyRule.policyStep;
        if (licensePolicyRule.licenseUseApprovalCd == G_LICENSE_USE_APPROVAL_DISAPPROVAL) {
          policyConflictColor = "darkred";
        }
      }
      licensePolicyResults.push({
        policyConflictColor,
        policyStepColor,
        policyStep,
        conflictDetails,
      });
    }
  }
  return licensePolicyResults;
};

export const renderComponentConflict = (currentData) => {
  const componentConflictResults = [];
  if (currentData) {
    for (let item of currentData) {
      const conflictDetail = getComponentLicenseConflict(currentData, item);
      componentConflictResults.push(conflictDetail);
    }
  }
  return componentConflictResults;
};

export const toTitleCase = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getColorByVulnerabilityLevel = (vulnerabilityLevel) => {
  switch (vulnerabilityLevel) {
    case 1:
      return "#CC1010"; // Red
    case 2:
      return "#E45B5B"; // Red
    case 3:
      return "#355DFF"; // Steel Blue
    case 4:
      return "#FFA84C"; // Orange
    default:
      return "#808080"; // Default gray
  }
};

// Convert primitive to option for CustomSelect value prop
export const asOption = (options, value) => {
  if (!value) return null;
  return options.find((o) => o.value === value) || null;
};

export const toKebabCase = (str) => {
  if (!str || typeof str !== "string") return "";

  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, "") // Remove special characters except hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

export const truncateText = (text, maxLength = 20) => {
  if (text.length <= maxLength) return text;
  
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 
    ? truncated.substring(0, lastSpace) + '...'
    : truncated + '...';
};

/**
 * Truncate text based on screen size with responsive limits
 * @param {string} text - Text to truncate
 * @param {number} smallScreenLimit - Character limit for small screens
 * @param {number} largeScreenLimit - Character limit for large screens
 * @param {number} breakpoint - Screen width breakpoint (default: 1400)
 * @param {number} screenWidth - Current screen width
 * @returns {string} Truncated text with '...' if needed
 */
export const truncateByScreenSize = (text, smallScreenLimit, largeScreenLimit, breakpoint = 1400, screenWidth = window.innerWidth) => {
  if (!text) return text;
  const maxLength = screenWidth < breakpoint ? smallScreenLimit : largeScreenLimit;
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const formatDateForRequest = (input) => {
  if (input instanceof Date) {
    return formatDate(input);
  } else {
    return input;
  }
};

// Format number with commas safely
export function formatNumberWithCommas(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return isNaN(num) ? "" : num.toLocaleString("en-US");
}

// Remove commas and return clean number string
export function unformatNumber(value) {
  if (!value) return "";
  return value.replace(/,/g, "");
}

// Remove all spaces from a string
export function removeSpaces(value) {
  if (!value) return "";
  return value.replace(/\s+/g, "");
}

/**
 * Normalizes components to have a unified componentSeq field
 * Creates a unique identifier in the format: ${projectSeq}-${seq}
 * Handles all component types: binaryScanComponentSeq, sourceCodeScanComponentSeq, sbomScanComponentSeq, mergedSbomComponentSeq
 * @param {Array} components - Array of component objects
 * @returns {Array} Array of components with normalized componentSeq field
 */
export const normalizeComponentsWithSeq = (components) => {
  if (!Array.isArray(components)) {
    return [];
  }
  return components.map((component) => ({
    ...component,
    componentSeq: component.binaryScanComponentSeq
      ? `${component.projectSeq}-${component.binaryScanComponentSeq}-clarity`
      : component.sourceCodeScanComponentSeq
        ? `${component.projectSeq}-${component.sourceCodeScanComponentSeq}-fossid`
        : component.sbomScanComponentSeq
          ? `${component.projectSeq}-${component.sbomScanComponentSeq}-sbom`
          : component.mergedSbomComponentSeq
            ? `${component.projectSeq}-${component.mergedSbomComponentSeq}-sbom-merged`
            : null,
  }));
};

// Check if number value is a valid number and within the range
export const displayValueInRange = (value, min = 1, max = 5) => {
  const numValue = Number(value);
  if (!isNaN(numValue) && numValue >= min && numValue <= max) {
    return value;
  }
  return "";
};

export const getBackgroundColor = (policyStepColor) => {
  switch (policyStepColor) {
    case "black":
      return "#000000";
    case "red":
      return "#CC1010";
    case "orange":
      return "#FF8300";
    case "green":
      return "#1C991A";
    case "steelblue":
      return "#355DFF";
    case "white":
    case COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION:
    default:
      return COLOR_CONFLICT_FLAG.UNDEFINED_OBLIGATION;
  }
};

/**
 * Sort array by a dynamic key with support for date/string/number comparisons
 * @param {Array} array - Array to sort
 * @param {string} key - Key to sort by
 * @param {string} order - Sort order: 'asc' or 'desc' (default: 'desc')
 * @param {string} type - Type of comparison: 'date', 'string', 'number' (default: 'date')
 * @returns {Array} Sorted array
 */
export const sortByKey = (array, key, order = 'desc', type = 'date') => {
  return [...array].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];

    // Handle null/undefined values
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    let comparison = 0;

    switch (type) {
      case 'date':
        comparison = new Date(bValue) - new Date(aValue);
        break;
      case 'string':
        comparison = bValue.localeCompare(aValue);
        break;
      case 'number':
        comparison = bValue - aValue;
        break;
      default:
        comparison = new Date(bValue) - new Date(aValue);
    }

    return order === 'asc' ? -comparison : comparison;
  });
};

export const isNotNullAndEmptyArray = (value) => {
  return value !== null && value !== undefined && value.length > 0;
};

export const isValidSshGitUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  const sshGitRegex = /^git@[^@\s]+:[^@\s]+\/[^@\s]+(\.git)?$/i;
  return sshGitRegex.test(trimmed);
};

export const isValidApiUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  // Reject URLs containing whitespace
  if (/\s/.test(trimmed)) return false;
  try {
    const urlObject = new URL(trimmed);
    return urlObject.protocol === "http:" || urlObject.protocol === "https:";
  } catch {
    return false;
  }
};

const getCveYear = (item) => {
  const cve = item?.cve;
  if (typeof cve !== "string") {
    return 0;
  }
  const match = /^CVE-(\d{4})-/i.exec(cve);
  return match ? Number(match[1]) : 0;
};

const normalizeSecuritiesForKey = (securities) => {
  if (!securities || securities === "[]") {
    return "";
  }

  try {
    let parsed = JSON.parse(securities);
    if (!Array.isArray(parsed)) {
      return securities;
    }
    if (parsed.length === 0) {
      return "";
    }
    const stripExploitForCompare = (item) => {
      if (!item || typeof item !== "object") {
        return item;
      }
      if (Array.isArray(item)) {
        return item.map(stripExploitForCompare);
      }

      return {
        cve: item?.cve,
        cvss_version: item?.cvss_version,
        severity: item?.severity,
        score: item?.score,
      };
    };
    const sorted = [...parsed].sort((a, b) => {
      const yearDiff = getCveYear(b) - getCveYear(a);
      if (yearDiff !== 0) {
        return yearDiff;
      }
      const cveDiff = (b?.cve ?? "").localeCompare(a?.cve ?? "");
      if (cveDiff !== 0) {
        return cveDiff;
      }
      return JSON.stringify(stripExploitForCompare(a ?? {})).localeCompare(
        JSON.stringify(stripExploitForCompare(b ?? {}))
      );
    });
    return JSON.stringify(sorted.map(stripExploitForCompare));
  } catch {
    return securities;
  }
};

const appendVexSourceContext = (target, source) => {
  if (source?.verificationSeq == null || !source?.verificationType) {
    return;
  }
  const contexts = target.vexSourceContexts ? [...target.vexSourceContexts] : [];
  const contextKey = `${source.verificationType}|${source.verificationSeq}`;
  if (!contexts.some((ctx) => `${ctx.verificationType}|${ctx.verificationSeq}` === contextKey)) {
    contexts.push({
      verificationSeq: source.verificationSeq,
      verificationType: source.verificationType,
      projectSeq: source.projectSeq,
      projectManagerId: source.projectManagerId,
      verificationStatusCd: source.verificationStatusCd,
    });
  }
  target.vexSourceContexts = contexts;
};

export const hasMissingComponentNames = (components) => {
  if (!Array.isArray(components)) return false;
  return components.some((item) => {
    const name = (item?.componentName || item?.component || "").toString().trim();
    return !name;
  });
};

/** Same identity used by component-list dedupe and Merged SBOM PK remap. */
export const buildComponentDedupeKey = (item) => {
  if (!item) {
    return "";
  }
  return [
    item.component || "",
    item.componentVersion || "",
    item.conflictFlag || "",
    item.projectLicenseConflictFlag || "",
    item.matchTypeCd || "",
    item.modificationFlag || "",
    item.noticeFlag || "",
    item.patentFlag || "",
    item.sourceOpenFlag || "",
    item.cvssScore || "",
    item.vulnerabilityLevel || "",
    normalizeSecuritiesForKey(item.securities),
  ].join("||");
};

export const deduplicateComponents = (components) => {
  if (!Array.isArray(components)) {
    return [];
  }
  const seen = new Map();

  components.forEach((item) => {
    const key = buildComponentDedupeKey(item);

    if (!seen.has(key)) {
      // Keep original securities for display order (Clarity/report keep scanner order).
      // normalizedSecurities is only for the dedupe key.
      const copy = { ...item };
      appendVexSourceContext(copy, item);
      seen.set(key, copy);
    } else {
      appendVexSourceContext(seen.get(key), item);
    }
  });
  return Array.from(seen.values());
};

/**
 * Formats verification type values to a consistent format.
 * Standardizes all variations to: "Clarity", "FossID", "SBOM"
 * 
 * @param {string} value - The verification type value (can be in any case or format)
 * @returns {string} - Formatted verification type: "Clarity", "FossID", or "SBOM"
 */
export const formatVerificationType = (value) => {
  if (!value || typeof value !== 'string') {
    return value || '-';
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'clarity') {
    return 'Clarity';
  }
  
  if (normalized === 'fossid' || normalized === 'foss-id' || normalized === 'foss_id') {
    return 'FossID';
  }
  
  if (normalized === 'sbom') {
    return 'SBOM';
  }

  if (normalized === 'clarityair' || normalized === 'clarity_air') {
    return 'ClarityAIR';
  }
  return value;
};

export const getScanToolHelper = (scanToolList = []) => {
  const keys = scanToolList.map(t => t.value.toLowerCase());

  return {
    hasTool: (key) => keys.includes(key),
  };
};

export const translateDepartment = (t, value) => {
  if (!value) return "";
  const normalized = value.toUpperCase().trim();
  const map = {

    DEV: "development-team",
    HUMAN_RESOURCE: "hr-team",
    RESEARCH_CENTER: "research-center",

    "DEVELOPMENT TEAM": "development-team",
    "HR TEAM": "hr-team",
    "RESEARCH CENTER": "research-center",
  };

  const key = map[normalized];

  return t(`common.code-name-department.${key}`, value);
};